import { z } from 'zod';

const identifier = z.string().regex(/^[a-z0-9][a-z0-9_]{1,63}$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const terms = z.array(z.string().trim().min(1).max(128)).max(128).default([]);
const dimension = z.number().int().positive().max(8192);
const relativePath = z
  .string()
  .min(1)
  .refine(
    (value) =>
      !value.startsWith('/') &&
      !value.includes('\\') &&
      !value.split('/').some((part) => part === '..' || part === '.' || !part),
    'Asset paths must be relative to the selected payload folder',
  );

const categorySchema = z.object({
  id: identifier,
  displayName: z.string().trim().min(1).max(128),
  sortOrder: z.number().int().min(0).max(999),
});

const stickerSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,63}__[a-z0-9][a-z0-9._-]{0,63}$/),
  name: z.string().trim().min(1).max(128),
  categoryId: identifier,
  sortOrder: z.number().int().min(0).max(100000),
  sourceId: z.string().trim().min(1).max(128).optional(),
  keywords: z.array(z.string().trim().min(1).max(128)).min(1).max(256),
  tags: terms,
  style: z.string().max(128).nullable().optional(),
  styleName: z.string().max(128).nullable().optional(),
  relatedCategoryIds: z.array(identifier).max(64).default([]),
  originalWidth: dimension,
  originalHeight: dimension,
  bytes: z
    .number()
    .int()
    .positive()
    .max(2 * 1024 * 1024),
  sha256,
  thumbnailWidth: z.number().int().positive().max(512),
  thumbnailHeight: z.number().int().positive().max(512),
  thumbnailBytes: z
    .number()
    .int()
    .positive()
    .max(256 * 1024),
  thumbnailSha256: sha256,
  sourcePath: relativePath,
  thumbnailPath: relativePath,
});

const manifestSchema = z.object({
  releaseId: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,63}$/),
  version: z.string().min(1).max(128),
  categories: z.array(categorySchema).min(1).max(64),
  stickers: z.array(stickerSchema).min(1).max(5000),
});

export type StickerImportManifest = z.infer<typeof manifestSchema>;
export type StickerImportAsset = StickerImportManifest['stickers'][number];
export type StickerReleaseManifest = Omit<StickerImportManifest, 'stickers'> & {
  stickers: Omit<StickerImportAsset, 'sourcePath' | 'thumbnailPath'>[];
};

export function parseStickerImportManifest(input: unknown): StickerImportManifest {
  const manifest = manifestSchema.parse(input);
  const categories = new Set(manifest.categories.map((item) => item.id));
  if (categories.size !== manifest.categories.length)
    throw new Error('Duplicate group ids in manifest');
  const ids = new Set<string>();
  const paths = new Set<string>();
  const hashes = new Set<string>();
  for (const sticker of manifest.stickers) {
    if (ids.has(sticker.id)) throw new Error(`Duplicate sticker id: ${sticker.id}`);
    ids.add(sticker.id);
    if (hashes.has(sticker.sha256)) throw new Error(`Duplicate sticker artwork: ${sticker.id}`);
    hashes.add(sticker.sha256);
    if (sticker.originalWidth * sticker.originalHeight > 16777216)
      throw new Error(`Decoded image is too large: ${sticker.id}`);
    if (!categories.has(sticker.categoryId))
      throw new Error(`Unknown group: ${sticker.categoryId}`);
    if (!sticker.id.startsWith(`${sticker.categoryId}__`))
      throw new Error(`Sticker id does not match its group: ${sticker.id}`);
    for (const related of sticker.relatedCategoryIds) {
      if (!categories.has(related)) throw new Error(`Unknown related group: ${related}`);
    }
    for (const path of [sticker.sourcePath, sticker.thumbnailPath]) {
      if (paths.has(path)) throw new Error(`Duplicate asset path: ${path}`);
      paths.add(path);
    }
  }
  return manifest;
}

export function releaseManifest(manifest: StickerImportManifest): StickerReleaseManifest {
  return {
    releaseId: manifest.releaseId,
    version: manifest.version,
    categories: manifest.categories,
    stickers: manifest.stickers.map(({ sourcePath, thumbnailPath, ...asset }) => {
      void sourcePath;
      void thumbnailPath;
      return asset;
    }),
  };
}

export function mapPayloadFiles(files: File[]): Map<string, File> {
  const result = new Map<string, File>();
  for (const file of files) {
    const path = file.webkitRelativePath || file.name;
    result.set(path, file);
    const slash = path.indexOf('/');
    if (slash >= 0) result.set(path.slice(slash + 1), file);
  }
  return result;
}

export function missingPayloadFiles(
  manifest: StickerImportManifest,
  files: Map<string, File>,
): string[] {
  return manifest.stickers
    .flatMap((asset) => [asset.sourcePath, asset.thumbnailPath])
    .filter((path) => !files.has(path));
}

export async function validatePayloadFile(
  file: File,
  bytes: number,
  expectedHash: string,
): Promise<void> {
  if (file.size !== bytes) throw new Error(`${file.name}: file size differs from the manifest`);
  const data = await file.arrayBuffer();
  const signature = new Uint8Array(data, 0, Math.min(8, data.byteLength));
  const png = [137, 80, 78, 71, 13, 10, 26, 10];
  if (signature.length !== 8 || png.some((byte, index) => signature[index] !== byte)) {
    throw new Error(`${file.name}: expected a PNG file`);
  }
  const digest = await crypto.subtle.digest('SHA-256', data);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  if (hash !== expectedHash) throw new Error(`${file.name}: checksum differs from the manifest`);
}
